import { SWRConfig } from 'swr';
import { unstable_serialize } from 'swr/infinite';
import { useRouter } from 'next/router';
import Head from 'next/head';
import FreeCourse from '@cont/FreePlatformCourse/FreePlatformCourse';
import useEntireFeed from '@cont/FreePlatformCourse/useEntireFeed';
import useTopology, {
  getKey as getTopologyKey
} from '@api/hooks/topology/useTopology';
import { fetchGoalInfo } from '@api/fetchers/goal';
import { fetchFreePlatformFeed, getFeedAPI } from '@api/fetchers/free-platform';
import { fetchConstants } from '@api/fetchers/constants';
import { isGTPGoal } from '@api/selectors/constant';
import freeCourseSD from '@seo/FreeCourseSD';
import Layout from '@layout/index';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import { useSetSelectedNav } from '@stores/sidenav';
import SEO from '@seo/index';
import { OTHER_COURSES } from '@constants/segment';

const FreeLiveClassesPageWithSEO = () => {
  const router = useRouter();
  const { goalUID } = router.query;

  const { topology: goalTopology } = useTopology(goalUID);
  const { data } = useEntireFeed({
    goalUID
  });

  const { goalSlug, title, uid, ogImage } = goalTopology;
  const goalTitle = `Prepare for ${title} from India’s best Educators.`;
  const goalDescription = `Prepare for ${title} from Top Educators with comprehensive, structured and crash courses along with practice section for ${title} for free.`;
  const canonicalURL = `https://unacademy.com/goal/${goalSlug}/${uid}/free`;
  const imageURL =
    ogImage ||
    'https://static.uacdn.net/static/dist/c76c2e18c7980818a913f9467c74c567.png';

  useSetSelectedNav('free-platform');

  return (
    <>
      <SEO
        title={goalTitle}
        description={goalDescription}
        openGraph={{
          title: goalTitle,
          description: goalDescription,
          images: [
            {
              url: imageURL
            }
          ],
          url: canonicalURL
        }}
        schema={freeCourseSD(goalTopology, data)}
      />
      <Head>
        <title>{goalTitle}</title>
      </Head>
      <FreeCourse />
    </>
  );
};

const FreeCoursePageWithSWRConfig = ({ platformFeedFallbackData, goal }) => {
  const key = unstable_serialize(() => getFeedAPI({ goalUID: goal.uid }));

  return (
    <SWRConfig
      value={{
        revalidateOnMount: false,
        dedupingInterval: 60 * 1000,
        fallback: {
          [key]: [platformFeedFallbackData]
        }
      }}
    >
      <FreeLiveClassesPageWithSEO />
    </SWRConfig>
  );
};

FreeCoursePageWithSWRConfig.Layout = Layout;

FreeCoursePageWithSWRConfig.Wrapper = PageReduxProvider;

FreeCoursePageWithSWRConfig.pageInfo = {
  isMWebSupported: true,
  name: OTHER_COURSES,
  layoutOptions: {
    showPlusNav: true
  }
};

export default FreeCoursePageWithSWRConfig;

export const getStaticProps = async ({ params }) => {
  try {
    const { goalUID, goalSlug } = params;

    const promises = [
      fetchGoalInfo(goalUID),
      fetchConstants(goalUID),
      fetchFreePlatformFeed({ goalUID })
    ];

    const [goal, constants, platformFeedFallbackData] = await Promise.all(
      promises
    );

    const isGTP = isGTPGoal(constants, goalUID);

    if (!goal?.uid) {
      return {
        redirect: {
          destination: '/'
        }
      };
    }

    if (isGTP) {
      return {
        redirect: {
          destination: `/goal/gtp/${goalSlug}/${goalUID}`
        }
      };
    }

    return {
      props: {
        goal,
        platformFeedFallbackData,
        globalFallbackData: {
          [getTopologyKey(goal.uid)]: goal
        }
      },
      revalidate: 5 * 60
    };
  } catch (err) {
    return { notFound: true, revalidate: 60 };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};
